(function () {
  if (window.RoyalFeaturedCollectionRuntime) {
    window.RoyalFeaturedCollectionRuntime.register(document);
    return;
  }

  function createFeaturedCollectionClass(id) {
    return class FeaturedCollection extends HTMLElement {
    connectedCallback() {
      this._id = id;
      this._autoplaySpeed = parseInt(this.dataset.autoplaySpeed) || 3000;
      this._autoplayEnabled = this.dataset.autoplay === 'true';
      this._autoplayVisible = true;
      this._ratingsVisible = false;

      // Init every panel as an independent slider
      this.querySelectorAll(`.ai-featured-collection__panel-${this._id}`).forEach(panel => {
        this._initPanel(panel);
      });

      // Tab switching
      this.querySelectorAll(`.ai-featured-collection__tab-${this._id}`).forEach(tab => {
        tab.addEventListener('click', () => this._switchTab(tab));
      });

      this._resizeHandler = () => {
        this.querySelectorAll(`.ai-featured-collection__panel-${this._id}`).forEach(panel => {
          this._handleResize(panel);
        });
      };
      window.addEventListener('resize', this._resizeHandler);

      this._visibilityHandler = () => this._syncAutoplay();
      document.addEventListener('visibilitychange', this._visibilityHandler);

      if ('IntersectionObserver' in window) {
        this._visibilityObserver = new IntersectionObserver(([entry]) => {
          this._autoplayVisible = entry.isIntersecting;
          if (entry.isIntersecting && !this._ratingsVisible) {
            this._ratingsVisible = true;
            this._materializeRatings(this);
          }
          this._syncAutoplay();
        }, { rootMargin: '200px 0px' });
        this._visibilityObserver.observe(this);
      } else {
        this._ratingsVisible = true;
        this._materializeRatings(this);
      }
    }

    /* ── Tab switch ── */
    _switchTab(tab) {
      const targetPanel = tab.dataset.panel;
      const panel = this._materializePanel(targetPanel);
      if (!panel) return;

      this.querySelectorAll(`.ai-featured-collection__tab-${this._id}`).forEach(t => {
        t.classList.remove('active');
      });
      tab.classList.add('active');

      this.querySelectorAll(`.ai-featured-collection__panel-${this._id}`).forEach(panel => {
        panel.classList.toggle('active', panel.dataset.panel === targetPanel);
      });

      this._syncAutoplay();
    }

    _materializePanel(targetPanel) {
      const panelSelector = `.ai-featured-collection__panel-${this._id}[data-panel="${targetPanel}"]`;
      let panel = this.querySelector(panelSelector);
      if (panel) return panel;

      const template = this.querySelector(`template[data-royal-panel-template="${targetPanel}"]`);
      if (!template || !template.content) return null;

      const fragment = template.content.cloneNode(true);
      panel = fragment.querySelector(panelSelector);
      template.replaceWith(fragment);

      if (panel) {
        this._initPanel(panel);
        if (this._ratingsVisible) this._materializeRatings(panel);
        document.dispatchEvent(new CustomEvent('royal:featured-panel:loaded', {
          detail: { panel, root: this }
        }));
      }

      return panel;
    }

    _materializeRatings(scope) {
      scope.querySelectorAll('[data-royal-rating-slot]').forEach((slot) => {
        const template = slot.querySelector('template');
        if (!template || !template.content) return;
        slot.replaceWith(template.content.cloneNode(true));
      });
    }

    /* ── Per-panel slider init ── */
    _initPanel(panel) {
      const state = {
        currentIndex: 0,
        isDragging: false,
        startPos: 0,
        currentTranslate: 0,
        prevTranslate: 0,
        animationID: null,
        autoplayInterval: null,
        dragMoved: false,
      };

      panel._sliderState = state;

      const slider = panel.querySelector(`.ai-featured-collection__slider-${this._id}`);
      const slides = panel.querySelectorAll(`.ai-featured-collection__slide-${this._id}`);
      const prevBtn = panel.querySelector(`.ai-featured-collection__nav-${this._id}--prev`);
      const nextBtn = panel.querySelector(`.ai-featured-collection__nav-${this._id}--next`);
      const dotsContainer = panel.querySelector(`.ai-featured-collection__dots-${this._id}`);

      if (!slider || slides.length === 0) return;

      state.slider = slider;
      state.slides = slides;
      state.prevBtn = prevBtn;
      state.nextBtn = nextBtn;
      state.dotsContainer = dotsContainer;
      state.slidesPerView = this._getSlidesPerView();
      state.maxIndex = Math.max(0, slides.length - state.slidesPerView);

      if (prevBtn) prevBtn.addEventListener('click', () => this._prev(panel));
      if (nextBtn) nextBtn.addEventListener('click', () => this._next(panel));

      // Touch / mouse drag — prevent link navigation when dragging
      slider.addEventListener('touchstart', e => this._touchStart(panel, e), { passive: true });
      slider.addEventListener('touchmove',  e => this._touchMove(panel, e),  { passive: false });
      slider.addEventListener('touchend',   e => this._touchEnd(panel, e));
      slider.addEventListener('mousedown',  e => this._touchStart(panel, e));
      slider.addEventListener('mousemove',  e => this._touchMove(panel, e));
      slider.addEventListener('mouseup',    e => this._touchEnd(panel, e));
      slider.addEventListener('mouseleave', e => this._touchEnd(panel, e));

      // Intercept clicks on product links during drag
      slider.addEventListener('click', e => {
        if (state.dragMoved) {
          e.preventDefault();
          state.dragMoved = false;
        }
      }, true);

      this.addEventListener('mouseenter', () => this._stopAutoplay(panel));
      this.addEventListener('mouseleave', () => {
        if (this._autoplayEnabled) this._startAutoplay(panel);
      });

      this._createDots(panel);
      this._updateButtons(panel);

      if (this._autoplayEnabled && panel.classList.contains('active')) {
        this._startAutoplay(panel);
      }
    }

    _getSlidesPerView() {
      const w = window.innerWidth;
      if (w <= 768)  return 2;
      if (w <= 1024) return 3;
      return 4;
    }

    _handleResize(panel) {
      const state = panel._sliderState;
      if (!state) return;
      const newPer = this._getSlidesPerView();
      if (newPer !== state.slidesPerView) {
        state.slidesPerView = newPer;
        state.maxIndex = Math.max(0, state.slides.length - newPer);
        state.currentIndex = Math.min(state.currentIndex, state.maxIndex);
        this._updateSlider(panel);
        this._createDots(panel);
      }
    }

    /* ── Drag / touch ── */
    _touchStart(panel, e) {
      const state = panel._sliderState;
      state.isDragging = true;
      state.dragMoved = false;
      state.startPos = this._getPosX(e);
      state.animationID = requestAnimationFrame(() => this._animate(panel));
      state.slider.style.cursor = 'grabbing';
      this._stopAutoplay(panel);
    }

    _touchMove(panel, e) {
      const state = panel._sliderState;
      if (!state.isDragging) return;
      const cur = this._getPosX(e);
      const diff = cur - state.startPos;
      state.currentTranslate = state.prevTranslate + diff;
      if (Math.abs(diff) > 5) {
        state.dragMoved = true;
        e.preventDefault();
      }
    }

    _touchEnd(panel, e) {
      const state = panel._sliderState;
      if (!state.isDragging) return;
      state.isDragging = false;
      cancelAnimationFrame(state.animationID);

      const moved = state.currentTranslate - state.prevTranslate;
      if (moved < -50 && state.currentIndex < state.maxIndex) state.currentIndex++;
      if (moved >  50 && state.currentIndex > 0)             state.currentIndex--;

      this._updateSlider(panel);
      state.slider.style.cursor = 'grab';
      if (this._autoplayEnabled) this._startAutoplay(panel);
    }

    _getPosX(e) {
      return e.type.includes('mouse') ? e.pageX : e.touches[0].clientX;
    }

    _animate(panel) {
      const state = panel._sliderState;
      state.slider.style.transform = `translateX(${state.currentTranslate}px)`;
      if (state.isDragging) requestAnimationFrame(() => this._animate(panel));
    }

    /* ── Slider state update ── */
    _updateSlider(panel) {
      const state = panel._sliderState;
      const slideWidth = state.slides[0].offsetWidth;
      const gap = 20;
      const offset = -(state.currentIndex * (slideWidth + gap));
      state.currentTranslate = offset;
      state.prevTranslate = offset;
      state.slider.style.transform = `translateX(${offset}px)`;
      this._updateButtons(panel);
      this._updateDots(panel);
    }

    _updateButtons(panel) {
      const { prevBtn, nextBtn, currentIndex, maxIndex } = panel._sliderState;
      if (prevBtn) prevBtn.disabled = currentIndex === 0;
      if (nextBtn) nextBtn.disabled = currentIndex >= maxIndex;
    }

    _createDots(panel) {
      const state = panel._sliderState;
      if (!state.dotsContainer) return;
      state.dotsContainer.innerHTML = '';
      const numDots = state.maxIndex + 1;
      for (let i = 0; i < numDots; i++) {
        const dot = document.createElement('button');
        dot.className = `ai-featured-collection__dot-${this._id}`;
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        if (i === 0) dot.classList.add('active');
        dot.addEventListener('click', () => {
          state.currentIndex = i;
          this._updateSlider(panel);
          this._stopAutoplay(panel);
          if (this._autoplayEnabled) this._startAutoplay(panel);
        });
        state.dotsContainer.appendChild(dot);
      }
    }

    _updateDots(panel) {
      const state = panel._sliderState;
      if (!state.dotsContainer) return;
      state.dotsContainer.querySelectorAll(`.ai-featured-collection__dot-${this._id}`).forEach((d, i) => {
        d.classList.toggle('active', i === state.currentIndex);
      });
    }

    _prev(panel) {
      const state = panel._sliderState;
      if (state.currentIndex > 0) { state.currentIndex--; this._updateSlider(panel); }
    }

    _next(panel) {
      const state = panel._sliderState;
      if (state.currentIndex < state.maxIndex) { state.currentIndex++; this._updateSlider(panel); }
    }

    /* ── Autoplay ── */
    _syncAutoplay() {
      this.querySelectorAll(`.ai-featured-collection__panel-${this._id}`).forEach(panel => {
        if (
          this._autoplayEnabled &&
          this._autoplayVisible &&
          !document.hidden &&
          panel.classList.contains('active')
        ) {
          this._startAutoplay(panel);
        } else {
          this._stopAutoplay(panel);
        }
      });
    }

    _startAutoplay(panel) {
      this._stopAutoplay(panel);
      const state = panel._sliderState;
      if (
        !state ||
        state.maxIndex === 0 ||
        !this._autoplayVisible ||
        document.hidden ||
        !panel.classList.contains('active')
      ) return;
      state.autoplayInterval = setInterval(() => {
        state.currentIndex = state.currentIndex >= state.maxIndex ? 0 : state.currentIndex + 1;
        this._updateSlider(panel);
      }, this._autoplaySpeed);
    }

    _stopAutoplay(panel) {
      const state = panel._sliderState;
      if (state && state.autoplayInterval) {
        clearInterval(state.autoplayInterval);
        state.autoplayInterval = null;
      }
    }

    disconnectedCallback() {
      this.querySelectorAll(`.ai-featured-collection__panel-${this._id}`).forEach(p => {
        this._stopAutoplay(p);
      });
      window.removeEventListener('resize', this._resizeHandler);
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      if (this._visibilityObserver) this._visibilityObserver.disconnect();
    }
    };
  }

  function register(scope) {
    const context = scope || document;
    context.querySelectorAll('[data-royal-featured-collection]').forEach((root) => {
      const name = root.localName;
      const id = root.dataset.aiGenId;
      if (id && !customElements.get(name)) {
        customElements.define(name, createFeaturedCollectionClass(id));
      }
    });
  }

  window.RoyalFeaturedCollectionRuntime = { register };
  document.addEventListener('shopify:section:load', (event) => register(event.target));
  register(document);
})();
